import { AxisLeft, AxisBottom } from '@vx/axis'
import { format } from 'd3-format'
import { scaleTime, scaleLinear, scaleSequential } from 'd3-scale'
import { interpolateCool } from 'd3-scale-chromatic'
import { area as areaShape, line as lineShape, curveMonotoneX } from 'd3-shape'
import { timeFormat } from 'd3-time-format'
import { motion } from 'framer-motion'
import { transparentize } from 'polished'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useMemo, useCallback, memo } from 'react'
import { usePrevious } from 'react-use'

import pendingMetricsSVGUrl from 'assets/pending_metrics.svg'
import ChartDataBoundryLines from 'components/charts/ChartDataBoundryLines'
import ChartTooltip, { useChartTooltip } from 'components/charts/ChartTooltip'
import ChartValueLine from 'components/charts/ChartValueLine'
import Grid from 'components/charts/Grid'
import Group from 'components/charts/Group'
import ResponsiveSVGWrapper from 'components/charts/ResponsiveSVGWrapper'
import ResponsiveSVG from 'components/charts/SVG'
import Image from 'components/common/Image'
import Text from 'components/common/Text'
import theme from 'theme'
import { track } from 'track'
import { METRICS_STATE } from 'utils/attack-metrics'
import { percentToDecimal } from 'utils/numbers'

const formatPercent = format('.0%')
const formatWholeNumber = format('d')
const formatTime = timeFormat('%X')

const chartColors = [
  theme.colors.green[0], // '#23C386'
  theme.colors.blue[0] // '#0f8ccf'
]

export const formatData = (attackMetrics, formatYValue, isComplete = false) => {
  // Manually convert our metrics api data to a format that d3-area understands
  // Uses same basic algorithm as https://github.com/d3/d3-shape#stacks
  // Use our green and blue for the first 2 clients and generated color scale for all others
  const colorScale = scaleSequential(interpolateCool).domain([
    0,
    Object.keys(attackMetrics).length - 2
  ])

  // Get the color of the client based on its index
  const getColor = (index) => {
    if (index < 2) {
      return chartColors[index]
    }

    return colorScale(index - 2)
  }

  const data = Object.entries(attackMetrics ?? {}).reduce(
    (output, [key, metrics], i) => {
      let sum = 0
      let max = 0
      const data = metrics.map(({ timestamp, value }) => {
        const yValue = formatYValue(value)
        sum += yValue
        max = Math.max(max, yValue)
        const datum = [0, yValue]
        datum.data = {
          timestamp: Number(timestamp),
          [key]: yValue,
          key: `${key}--${timestamp}`
        }
        return datum
      })
      data.key = key
      data.sum = sum
      data.max = max
      data.avg = sum / data.length
      data.color = getColor(i)
      output.push(data)
      return output
    },
    []
  )

  // Layer charts from highest avg value to lowest
  // so that lower values are rendered over the higher values
  return isComplete ? data.sort((a, b) => b.avg - a.avg) : data
}

const lastTs = (arr) => [
  arr[arr.length - 2]?.data?.timestamp ?? Date.now() - 5 * 60 * 1000, // 5 min ago
  arr[arr.length - 1]?.data?.timestamp ?? Date.now() - 5 * 60 * 1000 // 5 min ago
]

const AreaWithLineTop = memo(
  ({
    xScale,
    yScale,
    yMax,
    data,
    formatYTick,
    setTargetTooltip,
    clearTargetTooltip,
    isAdhocAttack
  }) => {
    const [prevTS, currentTS] = lastTs(data)
    const prevLastX = xScale(prevTS)
    const currentLastX = xScale(currentTS)
    const prevTSState = usePrevious(prevLastX) // Used to detect if this is first render

    // draw area
    const drawArea = areaShape()
      .x((d) => xScale(d.data.timestamp))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]))
      .curve(curveMonotoneX)

    // draw line at top of area
    const drawLine = lineShape()
      .x((d) => xScale(d.data.timestamp))
      .y((d) => yScale(d[1]))
      .curve(curveMonotoneX)

    const maskId = `mask-${data.key}`
    const duration = prevTSState ? (currentTS - prevTS) / 1000 : 2

    return (
      <Fragment>
        <mask id={maskId}>
          <motion.rect
            animate={{ width: currentLastX }}
            fill="white"
            height={yMax}
            initial={{
              width: 0
            }}
            transition={{
              type: 'easeIn',
              delay: 0.25,
              duration: duration
            }}
          />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path
            d={drawArea(data)}
            data-test-chart-path={data.key}
            fill={transparentize(0.5, data.color)}
          />

          <path
            d={drawLine(data)}
            fill="none"
            stroke={data.color}
            strokeWidth={2}
          />
          {data.map((d) => {
            const x = xScale(d.data.timestamp)
            const y = yScale(d[1])
            const formattedTimestamp = formatTime(d.data.timestamp)

            // Render hoverable circles on the line.
            // Render visible (smaller) circle and transparent (larger) hover target circle
            return (
              <Fragment key={d.data.key}>
                <circle
                  css={{
                    fillOpacity: 'var(--circle-opacity)'
                  }}
                  cx={x}
                  cy={y}
                  fill={data.color}
                  r={3}
                />
                <circle
                  css={{ cursor: 'crosshair' }}
                  cx={x}
                  cy={y}
                  fill="transparent"
                  r={8}
                  onMouseEnter={(event) => {
                    setTargetTooltip(event, {
                      title: `${data.key}`,
                      content: `${formattedTimestamp} - ${formatYTick(d[1])}`
                    })
                    //Chart Segment Metrics
                    const eventCategory = isAdhocAttack
                      ? 'AdhocAttackMetrics'
                      : 'ScenarioAttackMetrics'
                    track('TriggerDataPointTooltip', {
                      category: eventCategory
                    })
                  }}
                  onMouseLeave={clearTargetTooltip}
                />
              </Fragment>
            )
          })}
        </g>
      </Fragment>
    )
  }
)

AreaWithLineTop.propTypes = {
  xScale: PropTypes.func,
  yScale: PropTypes.func,
  formatYTick: PropTypes.func,
  data: PropTypes.array,
  yMax: PropTypes.number,
  setTargetTooltip: PropTypes.func,
  clearTargetTooltip: PropTypes.func,
  isAdhocAttack: PropTypes.bool
}

function MetricsSeriesGroup({
  xScale,
  yScale,
  yMax,
  series,
  formatYTick,
  setTargetTooltip,
  clearTargetTooltip,
  isAdhocAttack
}) {
  return (
    <Fragment>
      <Group
        clipPath="url(#RelevantDataClip)"
        css={{
          '--circle-opacity': 0.5,
          '&:hover': { '--circle-opacity': 1 }
        }}
      >
        {series.map((dataSet) => {
          return (
            <AreaWithLineTop
              key={dataSet.key}
              clearTargetTooltip={clearTargetTooltip}
              data={dataSet}
              formatYTick={formatYTick}
              isAdhocAttack={isAdhocAttack}
              setTargetTooltip={setTargetTooltip}
              xScale={xScale}
              yMax={yMax}
              yScale={yScale}
            />
          )
        })}
      </Group>
    </Fragment>
  )
}

MetricsSeriesGroup.propTypes = {
  xScale: PropTypes.func,
  yScale: PropTypes.func,
  yMax: PropTypes.number,
  series: PropTypes.array,
  metricsState: PropTypes.oneOf(Object.values(METRICS_STATE)),
  formatYTick: PropTypes.func,
  setTargetTooltip: PropTypes.func,
  clearTargetTooltip: PropTypes.func,
  isAdhocAttack: PropTypes.bool
}

const DEFAULT_CHART_TITLES = {
  shutdown: 'Number of Healthy Hosts',
  cpu: 'CPU Utilization',
  memory: 'Memory Utilization'
}

export default function AttackMetricsChart({
  attackType = '',
  metricsWithMeta,
  isLoadingData,
  darkMode,
  isAdhocAttack,
  magnitude,
  wasHalted,
  ...rest
}) {
  const { tooltipConfig, setTargetTooltip, clearTargetTooltip } =
    useChartTooltip({
      offsetTop: -theme.space[3], // aligns the tooltip about center line vertially
      offsetLeft: theme.space[1]
    })

  const {
    state: metricsState,
    range: metricsRange = {},
    attack_range: attackRange = {},
    metric_data: metrics = {}
  } = metricsWithMeta

  // destructure these separately because range is `null` in PENDING or NO_DATA state and destructuring a null object causes errors
  const startBounds = metricsRange?.start
  const endBounds = metricsRange?.end

  const startDate = attackRange?.start
  const endDate = attackRange?.end

  const isBooleanValueAttackType = attackType.toLowerCase() === 'shutdown'

  // chartGA : Send metrics info everytime the chart has loaded
  useEffect(() => {
    track(`${isAdhocAttack ? 'Adhoc' : 'Scenario'}AttackMetricsChartLoaded`, {
      category: isAdhocAttack ? 'AdhocAttackMetrics' : 'ScenarioAttackMetrics'
    })
  }, [isAdhocAttack])

  // Dimension calculations
  // These dimensions were chosen beacuse
  // they scale the svg document in a way that looks good in our various layouts.
  // We get a width/height ratio of 0.35 on the svg document
  const width = 880
  const height = 310
  const margin = { top: 20, right: 45, bottom: 30, left: 45 }
  const xMax = width - margin.left - margin.right
  const yMax = height - margin.top - margin.bottom

  // colors
  const labelColor = darkMode ? theme.colors.steel[0] : theme.colors.steel[1]
  const gridColor = transparentize(0.75, theme.colors.steel[1])
  const graphBorderColor = transparentize(0.25, theme.colors.steel[1])

  // Do we have enough metrics to render meaningful information
  const hasMetrics =
    metricsState === METRICS_STATE.PARTIAL ||
    metricsState === METRICS_STATE.COMPLETE

  const showLoadingAnimation =
    metricsState === METRICS_STATE.PENDING ||
    metricsState === METRICS_STATE.NOT_LOADED ||
    isLoadingData

  let formatYValue = useCallback(
    (v) => (isBooleanValueAttackType ? v : percentToDecimal(v, 2)),
    [isBooleanValueAttackType]
  )

  let formatYTick = useMemo(
    () => (isBooleanValueAttackType ? formatWholeNumber : formatPercent),
    [isBooleanValueAttackType]
  )
  let getMaxYDomain = useCallback(
    (maxVal) =>
      isBooleanValueAttackType
        ? Math.ceil(maxVal < 0 ? 1 : maxVal)
        : maxVal < 0
        ? 1
        : Math.min(maxVal + 0.1, 1),
    [isBooleanValueAttackType]
  )

  // data formatting
  const series = useMemo(
    () =>
      formatData(
        metrics,
        formatYValue,
        metricsState === METRICS_STATE.COMPLETE
      ),
    [formatYValue, metrics, metricsState]
  )

  // scales
  const xScale = useMemo(
    () => scaleTime().domain([startBounds, endBounds]).range([0, xMax]),
    [endBounds, startBounds, xMax]
  )

  let maxYValue = Math.max(...series.flatMap((data) => data.map((d) => d[1])))
  if (!isBooleanValueAttackType && magnitude) {
    maxYValue = Math.max(magnitude, maxYValue)
  }

  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, getMaxYDomain(maxYValue)])
        .range([yMax, 0])
        .nice(),
    [getMaxYDomain, maxYValue, yMax]
  )

  const xAxisTickValues = hasMetrics
    ? [
        startDate,
        startBounds,
        endBounds,
        // Only show end date if it is 15 seconds past start date to prevent overlap
        endDate > startDate + 15 * 1000 && endDate
      ].filter(Boolean)
    : []

  return (
    <ResponsiveSVGWrapper
      data-test-attack-metrics-chart
      css={{
        marginLeft: theme.space[5],
        marginRight: theme.space[5],
        backgroundColor: darkMode
          ? theme.colors.steel[2]
          : theme.colors.white[0],
        width: 'auto' // Override default width to respond better within OffCanvas and on-page contexts
      }}
      {...rest}
    >
      <Text
        data-test-chart-title
        as="h3"
        css={{
          color: darkMode ? theme.colors.steel[0] : theme.colors.steel[2],
          paddingBottom: theme.space[3]
        }}
      >
        {metricsWithMeta?.chart_title ??
          DEFAULT_CHART_TITLES[attackType.toLowerCase()]}
      </Text>
      <ResponsiveSVG
        ref={tooltipConfig.boundingElRef}
        height={height}
        width={width}
      >
        <Grid
          height={yMax}
          left={margin.left}
          numTicksColumns={12}
          numTicksRows={10}
          stroke={gridColor}
          strokeDasharray="2,2"
          top={margin.top}
          width={xMax}
          xScale={xScale}
          yScale={yScale}
        />
        <clipPath id="RelevantDataClip">
          <rect height={yMax} width={xMax} />
        </clipPath>
        <Group left={margin.left} top={margin.top}>
          <MetricsSeriesGroup
            clearTargetTooltip={clearTargetTooltip}
            formatYTick={formatYTick}
            isAdhocAttack={isAdhocAttack}
            series={series}
            setTargetTooltip={setTargetTooltip}
            xScale={xScale}
            yMax={yMax}
            yScale={yScale}
          />
          {hasMetrics && (
            <ChartDataBoundryLines
              endLabel={endDate ? (wasHalted ? 'HALTED' : 'END') : false}
              endStroke={
                wasHalted ? theme.colors.steel[1] : theme.colors.red[0]
              }
              endX={xScale(endDate)}
              height={yMax}
              startLabel="START"
              startX={xScale(startDate)}
            />
          )}
          {hasMetrics &&
            !isBooleanValueAttackType &&
            metricsWithMeta.lines &&
            metricsWithMeta.lines.map((l) => (
              <ChartValueLine
                key={`${l.label}-${l.value}`}
                color={labelColor}
                endX={xMax}
                label={l.label}
                yCoord={yScale(l.value)}
              />
            ))}
        </Group>
        <Group left={margin.left}>
          <AxisLeft
            left={0}
            numTicks={
              !isBooleanValueAttackType ? undefined : yScale.domain()[1]
            }
            scale={yScale}
            stroke={graphBorderColor}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text {...tickProps}>{formattedValue}</text>
            )}
            tickFormat={formatYTick}
            tickLabelProps={() => ({
              fill: labelColor,
              textAnchor: 'end',
              fontSize: 12,
              dx: '0.2em',
              dy: '0.1em'
            })}
            tickStroke="transparent"
            top={margin.top}
          />
          <AxisBottom
            left={0}
            scale={xScale}
            stroke={graphBorderColor}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text {...tickProps}>{formattedValue}</text>
            )}
            tickFormat={formatTime}
            tickLabelProps={() => ({
              fill: labelColor,
              textAnchor: 'end',
              fontSize: theme.fontSizes[0],
              dx: '2.5em',
              dy: '0.1em'
            })}
            tickStroke="transparent"
            tickValues={xAxisTickValues}
            top={height - margin.bottom}
          />
        </Group>
      </ResponsiveSVG>
      <ChartTooltip darkMode={darkMode} tooltipConfig={tooltipConfig} />
      {metricsState === METRICS_STATE.NO_DATA && (
        <div
          data-test-no-available-metrics
          css={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate3d(-50%, -50%, 0)',
            background: darkMode
              ? theme.colors.white[0]
              : theme.colors.steel[2],
            padding: theme.space[2],
            borderRadius: theme.borderRadius[0],
            textAlign: 'center'
          }}
        >
          <Text
            as="p"
            css={{
              color: darkMode ? theme.colors.steel[2] : theme.colors.white[0]
            }}
          >
            No metrics available.
          </Text>
        </div>
      )}
      {showLoadingAnimation && (
        // Display a pending animation if no interesting metrics are present
        // yet
        <Image
          css={{
            position: 'absolute',
            width: 180,
            top: '50%',
            left: '50%',
            transform: 'translate3d(-50%, -50%, 0)'
          }}
          src={pendingMetricsSVGUrl}
        />
      )}
    </ResponsiveSVGWrapper>
  )
}

AttackMetricsChart.propTypes = {
  attackType: PropTypes.string,
  isLoadingData: PropTypes.bool,
  metricsWithMeta: PropTypes.object,
  darkMode: PropTypes.bool,
  isAdhocAttack: PropTypes.bool,
  magnitude: PropTypes.number,
  wasHalted: PropTypes.bool
}
